<template>
  <div class="flex flex-col">
    <div class="flex items-center justify-between mb-4">
      <slot />
      <div class="flex items-center gap-2">
        <el-input
          placeholder="Search..."
          prefix-icon="el-icon-search"
          class="w-[250px]"
          clearable
          v-model="queryParams.search"
        />
        <el-button icon="el-icon-refresh" @click="refreshList" />
      </div>
    </div>
    <div
      class="flex flex-col ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-full overflow-auto space-y-4"
    >
      <div
        class="flex-1 flex flex-col rounded-lg border bg-card text-card-foreground shadow-2xs"
      >
        <div
          class="grid items-center font-semibold border-b p-3"
          style="
            grid-template-columns: 2fr 2fr 1.2fr 1.2fr 2fr 1.5fr 1.5fr;
            gap: 1rem;
          "
        >
          <div>Rule Name</div>
          <div>Device Type</div>
          <div>Event Source</div>
          <div>Cooldown (min)</div>
          <div>Recipients</div>
          <div>Status</div>
          <div>Actions</div>
        </div>
        <div class="flex-1 flex flex-col relative overflow-hidden">
          <SmartInfiniteCursorScroll
            :loadMethod="listNotificationRulesV2"
            :pageSize="20"
            listField="ruleList"
            :query-params="queryParams"
            ref="smartInfiniteCursorScrollRef"
            class="absolute inset-0"
          >
            <template #default="slotProps">
              <div
                v-if="slotProps && slotProps.item"
                class="grid items-center border-b py-2 px-3"
                style="
                  grid-template-columns: 2fr 2fr 1.2fr 1.2fr 2fr 1.5fr 1.5fr;
                  gap: 1rem;
                "
              >
                <div
                  class="font-semibold truncate"
                  :title="slotProps.item.rule_name"
                >
                  {{ slotProps.item.rule_name }}
                </div>
                <!-- Device Type -->
                <div
                  class="truncate"
                  :title="formatDeviceType(slotProps.item.type)"
                >
                  {{ formatDeviceType(slotProps.item.type) }}
                </div>
                <!-- Event Source (params_match.triggerType) -->
                <div class="truncate">
                  <span v-if="formatEventSource(slotProps.item).length">
                    {{ formatEventSource(slotProps.item).join(", ") }}
                  </span>
                  <span v-else>--</span>
                </div>
                <div
                  class="text-right truncate"
                  :title="String(slotProps.item.cooldown_min ?? '--')"
                >
                  {{ slotProps.item.cooldown_min ?? "--" }}
                </div>
                <!-- Recipients -->
                <div class="truncate" :title="formatRecipients(slotProps.item)">
                  {{ formatRecipients(slotProps.item) }}
                </div>
                <div
                  class="truncate"
                  :title="
                    slotProps.item.enabled === true ||
                    slotProps.item.enabled === 1
                      ? 'Enabled'
                      : 'Disabled'
                  "
                >
                  <div
                    class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    :class="{
                      'bg-green-100 text-green-800':
                        slotProps.item.enabled === true ||
                        slotProps.item.enabled === 1,
                      'bg-red-100 text-red-800':
                        slotProps.item.enabled === false ||
                        slotProps.item.enabled === 0,
                    }"
                  >
                    {{
                      slotProps.item.enabled === true ||
                      slotProps.item.enabled === 1
                        ? "Enabled"
                        : "Disabled"
                    }}
                  </div>
                </div>
                <div class="truncate" title="Actions">
                  <ActionButtons
                    @command="handleCommand"
                    :actions="actions"
                    :item="slotProps.item"
                  />
                </div>
              </div>
            </template>
          </SmartInfiniteCursorScroll>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  deleteNotificationRuleV2,
  listNotificationRulesV2,
  testNotificationRule,
} from "@/api";
import ActionButtons from "@/components/ActionButtons.vue";
import dayjs from "dayjs";
import SmartInfiniteCursorScroll from "./SmartInfiniteCursorScroll.vue";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import { Loading } from "element-ui";
export default {
  name: "AlertRules",
  components: {
    ActionButtons,
    SmartInfiniteCursorScroll,
    SmartSelect,
  },
  data() {
    return {
      queryParams: {
        search: "",
      },
      actions: [
        {
          type: "dropdown",
          label: "more",
          icon: "el-icon-more",
          items: [
            { label: "Edit Rule", command: "edit", icon: "el-icon-edit" },
            { label: "View Details", command: "view", icon: "el-icon-view" },
            {
              label: "Test Alert",
              command: "test",
              icon: "el-icon-s-promotion",
            },
            {
              label: "Delete Rule",
              command: "delete",
              icon: "el-icon-delete",
              color: "#F56C6C",
              divided: true,
            },
          ],
        },
      ],
    };
  },
  methods: {
    deleteNotificationRuleV2,
    listNotificationRulesV2,
    formatDeviceType(type) {
      if (type === "cameraAlert") return "Camera Alert";
      if (type === "lockAlarm") return "Lock Alarm";
      if (type === "watchAlarm") return "Watch Alarm";
      return type || "--";
    },
    formatEventSource(item) {
      if (!item || !item.params_match) return [];
      const match = item.params_match.triggerType;
      if (Array.isArray(match)) {
        return match.filter(Boolean);
      }
      if (typeof match === "string") {
        return match ? [match] : [];
      }
      return [];
    },
    formatRecipients(item) {
      if (!item || !Array.isArray(item.recipient_channels)) return "--";
      return item.recipient_channels
        .map((r) => r.recipient_name || r.recipient_id || "--")
        .join(", ");
    },
    async handleCommand({ command, item }) {
      if (command === "delete") {
        await this.handleDelete(item);
      } else if (command === "edit") {
        await this.handleEditModel(item);
      } else if (command === "view") {
        await this.handleViewDetail(item);
      } else if (command === "test") {
        await this.handleTest(item);
      }
    },
    async refreshList() {
      await this.$refs.smartInfiniteCursorScrollRef.refresh();
    },
    async handleDelete(item) {
      await this.$confirm(
        `Are you sure you want to delete rule ${item?.name}?`,
        "Warning",
        {
          type: "warning",
        }
      );
      await deleteNotificationRuleV2(item.rule_id);
      await this.refreshList();
    },
    async handleEditModel(item) {
      await this.$loaderWindow({
        title: `Edit Alert Rule`,
        component: () => import("./EditWhatsAppAlert.vue"),
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: { id: item.rule_id },
      });
      await this.refreshList();
    },
    async handleViewDetail(item) {
      await this.$loaderWindow({
        title: `View Alert Rule`,
        component: () => import("./DetailWhatsAppAlert.vue"),
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: { item },
      });
    },
    async handleTest(item) {
      const result = await this.$prompt(
        "Please enter test notification content",
        "Test Notification",
        {
          inputPlaceholder: "Enter content to include in the test notification",
          inputValue:
            "This is a test notification" + new Date().toLocaleString(),
          confirmButtonText: "Send",
          cancelButtonText: "Cancel",
          inputValidator: (value) => {
            if (!value || !value.trim()) {
              return "Content cannot be empty";
            }
            return true;
          },
        }
      );
      const loadingInstance = Loading.service({
        lock: true,
        text: "Sending test notification...",
        background: "rgba(0, 0, 0, 0.4)",
      });
      try {
        const response = await testNotificationRule({
          rule_id: item.rule_id,
          notification: {
            content: result.value,
            timestamp: dayjs().toISOString(),
            type: item.type,
          },
        });
        const html = this.formatTestResult(response);
        await this.$alert(html, "Test Notification Result", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "OK",
        });
      } finally {
        loadingInstance.close();
      }
    },
    formatTestResult(result) {
      if (!result) {
        return "<p>No response data.</p>";
      }
      // Legacy fallback while backend fully migrates to the new payload
      if (Array.isArray(result.messages)) {
        const rows = result.messages
          .map(
            (msg, index) =>
              `<div style="padding:8px 0;border-bottom:1px solid rgba(148,163,184,0.3);">
            <div><strong>#${index + 1}</strong> · ${
                msg.recipientName || msg.recipientId || "Recipient"
              }</div>
            <div>Status: ${msg.status || "--"} (${
                msg.success ? "success" : "failed"
              })</div>
            <div style="margin-top:4px;color:#94a3b8;">${
              msg.message || "--"
            }</div>
          </div>`
          )
          .join("");
        return `
      <div>
        <p><strong>Rule:</strong> ${
          result.ruleName || result.ruleId || "--"
        }</p>
        <div style="margin-top:8px;">${rows}</div>
      </div>`;
      }

      const providerIds = Array.isArray(result.provider_msg_ids)
        ? result.provider_msg_ids.filter(Boolean)
        : [];
      const providerIdsHtml = providerIds.length
        ? providerIds
            .map(
              (id, index) =>
                `<div style="padding:4px 0;border-bottom:1px solid rgba(148,163,184,0.2);"><strong>#${
                  index + 1
                }:</strong> ${id}</div>`
            )
            .join("")
        : '<p style="color:#94a3b8;">--</p>';
      const renderedMessage = (result.rendered_message || "--")
        .trim()
        .replace(/\n/g, "<br />");
      return `
    <div>
      <p><strong>Rule Matched:</strong> ${result.matched ? "Yes" : "No"}</p>
      <p><strong>Sent Count:</strong> ${result.sent_count ?? "--"}</p>
      <p><strong>Failed Count:</strong> ${result.failed_count ?? "--"}</p>
      <div style="margin-top:12px;">
        <p><strong>Provider Message IDs</strong></p>
        <div style="margin-top:4px;">${providerIdsHtml}</div>
      </div>
      <div style="margin-top:12px;">
        <p><strong>Rendered Message</strong></p>
        <div style="margin-top:4px;color:#94a3b8;">${renderedMessage}</div>
      </div>
    </div>`;
    },
  },
};
</script>
